import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Loader, Btn, Field, TextInput, SelectInput } from "../components/Primitives";
import { StatusBadge } from "../components/Badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["org_admin","dpo","legal_reviewer","security_officer","business_owner","vendor_manager","auditor"];

export default function UserManagement() {
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "dpo" });

  const load = async () => { const { data } = await api.get("/users"); setUsers(data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/users", form); toast.success("User created"); setOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggle = async (u) => {
    await api.put(`/users/${u.id}`, { is_active: !u.is_active });
    load();
  };

  if (!users) return <Loader />;

  return (
    <div>
      <PageHeader
        title="User Management"
        description="Manage workspace users and role-based access."
        testId="users-page-header"
        actions={<Btn data-testid="user-create-btn" onClick={() => setOpen(true)}><Plus className="w-4 h-4" /> New User</Btn>}
      />

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>{["Name","Email","Role","Status","Actions"].map(h => <th key={h} className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{h}</th>)}</tr>
          </thead>
          <tbody data-testid="users-table-body">
            {users.map(u => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5 font-medium">{u.name}</td>
                <td className="px-4 py-2.5 text-slate-600">{u.email}</td>
                <td className="px-4 py-2.5"><span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 border border-slate-200 rounded-sm">{u.role}</span></td>
                <td className="px-4 py-2.5"><StatusBadge status={u.is_active ? "active" : "closed"} /></td>
                <td className="px-4 py-2.5 text-right">
                  <Btn size="sm" variant="secondary" data-testid={`user-toggle-${u.id}`} onClick={() => toggle(u)}>{u.is_active ? "Deactivate" : "Activate"}</Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create User</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Name" required className="sm:col-span-2"><TextInput data-testid="user-field-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></Field>
            <Field label="Email" required><TextInput data-testid="user-field-email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></Field>
            <Field label="Password" required><TextInput data-testid="user-field-password" type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} /></Field>
            <Field label="Role"><SelectInput data-testid="user-field-role" value={form.role} onChange={(e) => setForm({...form, role: e.target.value})}>{ROLES.map(o => <option key={o} value={o}>{o}</option>)}</SelectInput></Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn data-testid="user-save-btn" onClick={create}>Create</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
